/**
 * Platform seam.
 *
 * Feature code must never touch a browser API directly. Everything that behaves
 * differently between a browser tab and a desktop window goes through here, so
 * that packaging the app as a Tauri .exe later is a build step rather than a
 * hunt through forty screens.
 *
 * The five things that actually differ, and are handled here:
 *   storage       localStorage is sandboxed differently inside a webview
 *   file saving   <a download> works in a browser; desktop needs the FS API
 *   sharing       Web Share API exists in browsers, not in a desktop shell
 *   external URLs a desktop window must not navigate away from the app
 *   printing      same today, but a single call site is cheap insurance
 *
 * See CLAUDE.md — "Keep the desktop build working".
 */

export type PlatformKind = 'browser' | 'desktop';

/** Tauri injects this global; its absence means we are in a normal browser. */
export const platform: PlatformKind =
  typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window ? 'desktop' : 'browser';

export const isDesktop = platform === 'desktop';

/* -------------------------------------------------------------------------- */
/* Storage                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Small key/value store for preferences — theme, last-used site, table density.
 * Never used for anything that must survive, and never for secrets: Supabase
 * owns the session.
 *
 * Wrapped in try/catch because storage throws outright in private windows and
 * under some enterprise policies, and a theme preference is not worth crashing
 * the application over.
 */
export const store = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(`vns.${key}`) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(`vns.${key}`, value);
    } catch {
      /* preferences are best-effort */
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(`vns.${key}`);
    } catch {
      /* ignore */
    }
  },
};

export const sessionStore = {
  get(key: string): string | null {
    try {
      return globalThis.sessionStorage?.getItem(`vns.${key}`) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.sessionStorage?.setItem(`vns.${key}`, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      globalThis.sessionStorage?.removeItem(`vns.${key}`);
    } catch {
      /* ignore */
    }
  },
};

/* -------------------------------------------------------------------------- */
/* Saving files — every Excel and PDF export goes through this                */
/* -------------------------------------------------------------------------- */

export async function saveFile(
  filename: string,
  data: Blob | ArrayBuffer | string,
  mime = 'application/octet-stream',
): Promise<void> {
  const blob =
    data instanceof Blob ? data : new Blob([data as BlobPart], { type: mime });

  if (isDesktop) {
    // Replaced with Tauri's save dialog when the desktop target is added; the
    // browser path still works inside a webview, so this is not urgent.
    return browserDownload(filename, blob);
  }
  return browserDownload(filename, blob);
}

function browserDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/* -------------------------------------------------------------------------- */
/* Sharing — "download or share" appears on every report in the requirements  */
/* -------------------------------------------------------------------------- */

export function canShareFiles(): boolean {
  return (
    !isDesktop &&
    typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' &&
    typeof navigator.share === 'function'
  );
}

/** Returns false when sharing is unavailable or the user dismisses the sheet. */
export async function shareFile(
  filename: string,
  data: Blob,
  title: string,
): Promise<boolean> {
  if (!canShareFiles()) return false;
  const file = new File([data], filename, { type: data.type });
  if (!navigator.canShare({ files: [file] })) return false;
  try {
    await navigator.share({ files: [file], title });
    return true;
  } catch {
    return false; // AbortError when dismissed — not an error worth surfacing
  }
}

/* -------------------------------------------------------------------------- */
/* External links                                                             */
/* -------------------------------------------------------------------------- */

/**
 * A desktop window navigating to an external site would replace the app with no
 * way back, so links always open outside it.
 */
export function openExternal(url: string): void {
  if (!/^https?:\/\//i.test(url)) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function printPage(): void {
  window.print();
}

/* -------------------------------------------------------------------------- */
/* PWA update — reload onto a new deploy automatically, but never while       */
/* someone has unsaved work in progress (a drawer open, or a dirty form like  */
/* Settings).                                                                 */
/* -------------------------------------------------------------------------- */

let activeForms = 0;
let updateWaiting = false;

/** Called whenever a drawer opens, or a standalone form becomes dirty. */
export function formOpened(): void {
  activeForms += 1;
}

/** Called whenever that drawer closes, or the form is saved/discarded. */
export function formClosed(): void {
  activeForms = Math.max(0, activeForms - 1);
  if (activeForms === 0 && updateWaiting) {
    window.location.reload();
  }
}

/** Called once a new service worker has taken control of the page. */
export function reloadWhenIdle(): void {
  updateWaiting = true;
  if (activeForms === 0) {
    window.location.reload();
  }
}
